import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  formatToLocaleNumber,
  getIconByName,
  showToastNotification,
} from "src/utils";
import styles from "./TrendAlerts.module.scss";
import { Table } from "src/components/Table/Table";
import { selectUser } from "src/store/selectors";
import { useModal } from "src/hooks";
import { TrendAlertModal } from "src/features/Modals/TrendAlertModal/TrendAlertModal";
import { fetchTrendAlerts } from "src/store/trendAlerts/trendAlertsSlice";
import { useAppDispatch } from "src/store";
import {
  selectTrendAlertsByAuthorId,
  selectTrendAlertsStatus,
} from "src/store/trendAlerts/trendAlertsSelector";
import { MembersSelect } from "src/features/Modals/TrendAlertModal/components/MembersSelect/MembersSelect";
import { Preloader, Tabs, Tooltip } from "src/components";
import { TrendAlertItem } from "./types";
import {
  NEW_DATA_ALERTS_TABLE_COLUMNS,
  VOLUME_ALERTS_TABLE_COLUMNS,
} from "./constants";
import {
  partitionAlerts,
  formatVolumeDisplay,
} from "src/features/Modals/TrendAlertModal/utils";

export const TrendAlerts: FC = () => {
  const currentUser = useSelector(selectUser);
  const [selectedUserId, setSelectedUserId] = useState<string>(currentUser.id);
  const [activeTab, setActiveTab] = useState<string>("volume");

  const { setModal } = useModal();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const allAlerts = useSelector((state: Store.RootState) =>
    selectTrendAlertsByAuthorId(state, selectedUserId),
  );
  const isLoading = useSelector(
    (state: Store.RootState) => selectTrendAlertsStatus(state) === "loading",
  );

  const getTrendAlerts = useCallback(
    async (authorId: string) => {
      try {
        await dispatch(fetchTrendAlerts({ authorId })).unwrap();
      } catch (error) {
        console.error(t("component.trend_alerts.error"), error);
        showToastNotification({
          type: "error",
          text: t("component.trend_alerts.error"),
        });
      }
    },
    [dispatch, t],
  );

  useEffect(() => {
    if (selectedUserId) {
      getTrendAlerts(selectedUserId);
    }
  }, [selectedUserId, getTrendAlerts]);

  const { volumeList, newDataList } = useMemo(() => {
    const { volumeAlerts, newDataAlerts } = partitionAlerts(allAlerts);

    const groupedVolumeAlerts = volumeAlerts.reduce(
      (acc: Record<string, TrendAlertItem>, alert) => {
        if (!acc[alert.trackerName]) {
          const alertsNumber = volumeAlerts.filter(
            (a) => a.trackerId === alert.trackerId,
          ).length;

          acc[alert.trackerName] = {
            ...alert,
            name: alert.trackerName,
            percentage: alertsNumber === 1 ? alert.percentage?.toString() : "",
            condition:
              alertsNumber === 1 ? (
                alert.condition
              ) : (
                <Tooltip content={t("component.trend_alerts.tooltip")}>
                  {alertsNumber} alerts set
                </Tooltip>
              ),
            currentVolume: alert.currentVolume || alert.baseVolume,
            targetVolume: alertsNumber === 1 ? alert.targetVolume : null,
          };
        }
        return acc;
      },
      {},
    );

    const groupedNewDataAlerts = newDataAlerts.reduce(
      (acc: Record<string, TrendAlertItem>, alert) => {
        if (!acc[alert.trackerName]) {
          const alertsNumber = newDataAlerts.filter(
            (a) => a.trackerId === alert.trackerId,
          ).length;

          acc[alert.trackerName] = {
            ...alert,
            name: alert.trackerName,
            condition:
              alertsNumber === 1 ? (
                alert.condition
              ) : (
                <Tooltip content={t("component.trend_alerts.tooltip")}>
                  {alertsNumber} alerts set
                </Tooltip>
              ),
          };
        }
        return acc;
      },
      {},
    );

    const volumeListNext = Object.values(groupedVolumeAlerts);
    const newDataListNext = Object.values(groupedNewDataAlerts);

    return {
      volumeList: volumeListNext,
      newDataList: newDataListNext,
    };
  }, [allAlerts, t]);

  useEffect(() => {
    if (!volumeList.length && newDataList.length && activeTab === "volume") {
      setActiveTab("newData");
    }
  }, [volumeList.length, newDataList.length, activeTab]);

  const handleEdit = useCallback(
    (trackerId: string) => {
      setModal(
        "trend-alert",
        <TrendAlertModal trackerId={trackerId} currentTab={activeTab} />,
      );
    },
    [setModal, activeTab],
  );

  const formattedList = useMemo(() => {
    const source = activeTab === "volume" ? volumeList : newDataList;
    return source.map((item) => {
      const baseItem: any = {
        ...item,
        edit: (
          <div
            className={styles.icon}
            onClick={() => handleEdit(item.trackerId)}
          >
            {getIconByName("Pencil")}
          </div>
        ),
      };

      baseItem.currentVolume = item.currentVolume
        ? formatToLocaleNumber(item.currentVolume)
        : "-";
      baseItem.targetVolume = item.targetVolume
        ? formatVolumeDisplay(item.targetVolume, item.type, item.percentage)
        : "-";

      return baseItem;
    });
  }, [activeTab, volumeList, newDataList, handleEdit]);

  return (
    <div className={styles.container}>
      {["admin", "owner"].includes(currentUser.companyRole) ? (
        <MembersSelect
          onSelect={setSelectedUserId}
          selectedValue={selectedUserId}
        />
      ) : null}
      <div className={styles.buttonsWrapper}>
        <Tabs
          className={styles.tabs}
          activeTab={activeTab}
          tabs={[
            {
              value: "volume",
              label: t("component.modal.trend_alerts.tab.volume"),
              onSelect: setActiveTab,
            },
            {
              value: "newData",
              label: t("component.modal.trend_alerts.tab.new_data"),
              onSelect: setActiveTab,
            },
          ]}
        />
      </div>
      {isLoading ? (
        <div className={styles.loader}>
          <Preloader />
        </div>
      ) : (
        <Table
          type="trends"
          items={formattedList}
          tableColumns={
            activeTab === "newData"
              ? NEW_DATA_ALERTS_TABLE_COLUMNS
              : VOLUME_ALERTS_TABLE_COLUMNS
          }
          defaultSort={{ updatedAt: "DESC" }}
          emptyTableLabel={
            selectedUserId === currentUser.id ? (
              <>
                You haven't created any alerts yet.
                <br />
                <br />
                To create one, go to your dashboard, click <b>Edit Topic</b> for
                the topic you're interested in,
                <br />
                and choose <b>Alerts</b> from the three-dot menu.
              </>
            ) : (
              t("component.trend_alerts.no_alerts")
            )
          }
        />
      )}
    </div>
  );
};
